import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export type SubscriptionStatus = 'free' | 'trial' | 'active' | 'past_due' | 'cancelled' | 'lifetime';
export type PaymentProvider = 'manual' | 'stripe' | 'yookassa' | 'lifetime';
export type CustomerType = 'user' | 'team';

export interface AdminSubscription {
  id: string;
  customerType: CustomerType;
  customerId: string;
  planCode: string;
  status: SubscriptionStatus;
  paymentProvider: PaymentProvider;
  seatCount: number;
  currentPeriodStart: string;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
  grantReason: string | null;
  grantedBy: string | null;
  createdAt: string;
}

export interface ListSubscriptionsResult {
  total: number;
  items: AdminSubscription[];
}

export interface ListSubscriptionsQuery {
  limit?: number;
  offset?: number;
  status?: SubscriptionStatus;
  paymentProvider?: PaymentProvider;
  planCode?: string;
  customerType?: CustomerType;
}

function buildQS(q: ListSubscriptionsQuery): string {
  const sp = new URLSearchParams();
  if (q.limit !== undefined) sp.set('limit', String(q.limit));
  if (q.offset !== undefined) sp.set('offset', String(q.offset));
  if (q.status) sp.set('status', q.status);
  if (q.paymentProvider) sp.set('paymentProvider', q.paymentProvider);
  if (q.planCode) sp.set('planCode', q.planCode);
  if (q.customerType) sp.set('customerType', q.customerType);
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function adminSubsKey(query: ListSubscriptionsQuery = {}): QueryKey {
  return ['admin', 'subscriptions', query];
}

export function useAdminSubscriptions(query: ListSubscriptionsQuery) {
  return useQuery({
    queryKey: adminSubsKey(query),
    queryFn: () => api<ListSubscriptionsResult>(`/api/admin/subscriptions${buildQS(query)}`),
    placeholderData: (prev) => prev,
  });
}

/**
 * Single-subscription fetch — backs the /subscriptions/$id detail page.
 *
 * Server-side this calls `getById` which audit-logs the read against the
 * *customer* (user / team), not the subscription itself, so it shows up in
 * Account → Privacy → Operator access for the affected user.  See
 * subscriptions-admin.service.ts for the rationale.
 */
export function useAdminSubscription(subscriptionId: string | null | undefined) {
  return useQuery({
    queryKey: ['admin', 'subscription', subscriptionId],
    queryFn: () =>
      api<AdminSubscription>(`/api/admin/subscriptions/${subscriptionId}`),
    enabled: !!subscriptionId,
    staleTime: 10_000,
  });
}

export interface GrantInput {
  customerType: CustomerType;
  customerId: string;
  planCode: string;
  /** Null = lifetime. */
  durationDays: number | null;
  reason: string;
}

export function useGrantSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GrantInput) =>
      api<AdminSubscription>('/api/admin/subscriptions/grant', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useExpireSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<{ accepted: true }>(`/api/admin/subscriptions/${id}/expire`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });
}

export function useExtendSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, additionalDays, reason }: { id: string; additionalDays: number; reason: string }) =>
      api<AdminSubscription>(`/api/admin/subscriptions/${id}/extend`, {
        method: 'POST',
        body: { additionalDays, reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });
}
